import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React from 'react';
import PropTypes from 'prop-types';
import Loader from '@sprinx/react-loader/Loader';
import CldrSupplementals from '../CldrSupplementals';
import GlobalizeContext from './GlobalizeContext';
import useLocaleManager from './useLocaleManager';
import useSupportedLocalesNormalizer from './useSupportedLocalesNormalizer';
import useCheckAllMessages from './useCheckAllMessages';
import useChangeGlobalizeLocale from './useChangeGlobalizeLocale';
import LocaleCldrSelector from './LocaleCldrSelector';
import MessagesSelector from './MessagesSelector';
import GlobalizeInitializator from './GlobalizeInitializator';
var STORAGE_KEY = 'preferredLocale';

var _ref2 = /*#__PURE__*/React.createElement(Loader, {
  loading: true
});

function GlobalizeProvider(_ref) {
  var cache = _ref.cache,
      children = _ref.children,
      cldrs = _ref.cldrs,
      defaultLocale = _ref.defaultLocale,
      disableCurrency = _ref.disableCurrency,
      disableDate = _ref.disableDate,
      disableNumber = _ref.disableNumber,
      disablePlural = _ref.disablePlural,
      disableRelativeTime = _ref.disableRelativeTime,
      disableTerritories = _ref.disableTerritories,
      disableUnits = _ref.disableUnits,
      dontResolveLocale = _ref.dontResolveLocale,
      loadSetting = _ref.loadSetting,
      messages = _ref.messages,
      handlePreferredLocaleChanged = _ref.onPreferredLocaleChanged,
      pSupportedLocales = _ref.supportedLocales;
  var supportedLocales = useSupportedLocalesNormalizer(pSupportedLocales);
  var messagesError = useCheckAllMessages(supportedLocales, messages);
  var changeGlobalizeLocale = useChangeGlobalizeLocale();

  var _useLocaleManager = useLocaleManager(STORAGE_KEY, dontResolveLocale, defaultLocale, loadSetting, cache),
      _useLocaleManager2 = _slicedToArray(_useLocaleManager, 4),
      preferredLocale = _useLocaleManager2[0],
      localeError = _useLocaleManager2[1],
      loading = _useLocaleManager2[2],
      setPreferredLocale = _useLocaleManager2[3];

  var changePreferredLocale = React.useCallback(function (nextPreferredLocale) {
    changeGlobalizeLocale(supportedLocales, nextPreferredLocale);
    setPreferredLocale(nextPreferredLocale);
    if (handlePreferredLocaleChanged) handlePreferredLocaleChanged(nextPreferredLocale);
  }, [changeGlobalizeLocale, handlePreferredLocaleChanged, setPreferredLocale, supportedLocales]);
  if (messagesError || localeError) throw messagesError || localeError;
  return loading ? _ref2 : /*#__PURE__*/React.createElement(MessagesSelector, {
    messages: messages,
    locale: preferredLocale
  }, function (Messages) {
    return /*#__PURE__*/React.createElement(Messages, null, function (localeMessages) {
      return /*#__PURE__*/React.createElement(CldrSupplementals, {
        disableCurrency: disableCurrency,
        disableDate: disableDate,
        disableNumber: disableNumber,
        disablePlural: disablePlural,
        disableRelativeTime: disableRelativeTime,
        disableTerritories: disableTerritories,
        disableUnits: disableUnits
      }, function (cldrSupplementals) {
        return /*#__PURE__*/React.createElement(LocaleCldrSelector, {
          cldrs: cldrs,
          locale: preferredLocale
        }, function (LocaleCldr) {
          return /*#__PURE__*/React.createElement(LocaleCldr, {
            cldrData: cldrSupplementals,
            disableCurrency: disableCurrency,
            disableDate: disableDate,
            disableNumber: disableNumber,
            disablePlural: disablePlural,
            disableRelativeTime: disableRelativeTime,
            disableTerritories: disableTerritories,
            disableUnits: disableUnits
          }, function (cldrData) {
            return /*#__PURE__*/React.createElement(GlobalizeInitializator, {
              cldrData: cldrData,
              locale: preferredLocale,
              messages: localeMessages
            }, /*#__PURE__*/React.createElement(GlobalizeContext.Provider, {
              value: {
                preferredLocale: preferredLocale,
                changePreferredLocale: changePreferredLocale
              }
            }, children));
          });
        });
      });
    });
  });
}

process.env.NODE_ENV !== "production" ? GlobalizeProvider.propTypes = {
  /**
   * Cache. Pokud neni definovan pouzije se `localStorage`
   */
  cache: PropTypes.shape({
    getItem: PropTypes.func.isRequired,
    setItem: PropTypes.func.isRequired
  }),
  children: PropTypes.node,
  cldrs: PropTypes.shape({}).isRequired,

  /**
   * URL k cldr serveru.
   */
  // cldrServerHost: PropTypes.string.isRequired,

  /**
   * Kod defaultniho preferovaneho jazyku.
   */
  defaultLocale: PropTypes.string,
  disableCurrency: PropTypes.bool,
  disableDate: PropTypes.bool,
  disableNumber: PropTypes.bool,
  disablePlural: PropTypes.bool,
  disableRelativeTime: PropTypes.bool,
  disableTerritories: PropTypes.bool,
  disableUnits: PropTypes.bool,

  /**
   * If `true` don't run resolving locale and use value of `defaultLocale`.
   * Usable for using with `react-static`.
   */
  dontResolveLocale: PropTypes.bool,

  /**
   * Funkce, ktera vraci jazyk z API, pripadne jineho zdroje nastaveni mimo vlastni aplikaci.
   *
   * Segnatura: `() => Promise<string>`
   */
  loadSetting: PropTypes.func,

  /**
   * Hash s komponenty prekladu. `key` je locale, `value` je komponenta s preklady.
   */
  messages: PropTypes.shape({}).isRequired,

  /**
   * Hanlder, ktery se zavola po zmene preferovaneho jazyku.
   *
   * Pokud je potreba reagovat na zmenu jazuku v nadrazenych elementech.
   *
   * Signatura funkce `(locale) => void`
   */
  onPreferredLocaleChanged: PropTypes.func,

  /**
   * Render function. `(props) => Node`.
   */
  // render: PropTypes.func.isRequired,

  /**
   * Seznam podporovanych jazyku aplikace.
   */
  supportedLocales: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    code: PropTypes.string,
    label: PropTypes.string
  })]))
} : void 0;
GlobalizeProvider.defaultProps = {
  cache: undefined,
  children: null,
  defaultLocale: 'en',
  disableCurrency: false,
  disableDate: false,
  disableNumber: false,
  disablePlural: false,
  disableRelativeTime: true,
  disableTerritories: false,
  disableUnits: false,
  dontResolveLocale: false,
  loadSetting: undefined,
  onPreferredLocaleChanged: undefined,
  supportedLocales: ['en']
};
export default GlobalizeProvider;